/**
 * <ry-switch>
 *
 * Toggle switch component.
 *
 * Usage:
 * <ry-switch name="darkMode">Dark mode</ry-switch>
 * <ry-switch name="notifications" checked>Enable notifications</ry-switch>
 *
 * JS uses data-ry-target for queries, CSS uses classes for styling.
 */

import { RyElement } from '../core/ry-element.js';
import type { ChangeDetail } from '../types.js';

let switchIdCounter = 0;

export class RySwitch extends RyElement {
  #input: HTMLInputElement | null = null;

  static observedAttributes = ['checked', 'disabled'] as const;

  setup(): void {
    this.#transformMarkup();

    this.#input = this.$<HTMLInputElement>('input[type="checkbox"]');
    if (this.#input) {
      this.on(this.#input, 'change', this.#handleChange);
    }

    // Keyboard support on the wrapper
    this.on(this, 'keydown', this.#handleKeydown);
  }

  #transformMarkup(): void {
    // Check if already transformed
    if (this.$('[data-ry-target="track"]')) return;

    const id = `ry-switch-${++switchIdCounter}`;
    const name = this.getAttribute('name') ?? '';
    const checked = this.hasAttribute('checked');
    const disabled = this.hasAttribute('disabled');
    const label = this.textContent?.trim() ?? '';

    // data-ry-target for JS queries, class for CSS styling
    this.innerHTML = `
      <label data-ry-target="wrapper" class="ry-switch__wrapper" for="${id}">
        <input
          type="checkbox"
          id="${id}"
          data-ry-target="input"
          class="ry-switch__input"
          ${name ? `name="${name}"` : ''}
          ${checked ? 'checked' : ''}
          ${disabled ? 'disabled' : ''}
          role="switch"
          aria-checked="${checked}"
        >
        <span data-ry-target="track" class="ry-switch__track">
          <span data-ry-target="thumb" class="ry-switch__thumb"></span>
        </span>
        ${label ? `<span data-ry-target="label" class="ry-switch__label">${label}</span>` : ''}
      </label>
    `;
  }

  #handleChange = (): void => {
    if (!this.#input) return;

    const checked = this.#input.checked;
    this.#input.setAttribute('aria-checked', String(checked));

    if (checked) {
      this.setAttribute('checked', '');
    } else {
      this.removeAttribute('checked');
    }

    this.emit<ChangeDetail>('change', {
      value: String(checked),
      label: checked ? 'on' : 'off'
    });
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.#input?.click();
    }
  };

  get checked(): boolean {
    return this.#input?.checked ?? false;
  }

  set checked(value: boolean) {
    if (this.#input) {
      this.#input.checked = value;
      this.#input.setAttribute('aria-checked', String(value));

      if (value) {
        this.setAttribute('checked', '');
      } else {
        this.removeAttribute('checked');
      }
    }
  }

  get value(): string {
    return this.#input?.value ?? '';
  }

  set value(val: string) {
    if (this.#input) {
      this.#input.value = val;
    }
  }
}

customElements.define('ry-switch', RySwitch);
